import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  useUpDateModifierMutation,
  useGetModifierOptionInfoMutation,
} from "redux/modifiers/service";
import { errorHandler } from "api/api/errorHandler";
import { isValidValue } from "utils/Utils";
import { UTILS } from "utils";
import AppContainer from "components/ui/AppContainer";
import ModifierForm from "./components/ModifierForm";
import AppModal from "components/ui/AppModal";

const UpdateModifier = ({
  onSuccess = () => {},
  as = "update",
  onClose = () => {},
  open = false,
  selectedModifier = {},
  title = "Update Modifier",
  subTitle = "Please enter complete details for better user experience",
}) => {
  // Hooks
  const { t } = useTranslation();
  const history = useHistory();

  const [upDateModifier] = useUpDateModifierMutation();
  const [getModifierOptionInfo] = useGetModifierOptionInfoMutation();
  // States
  const [loading, setLoading] = useState(false);


  const [modifier, setModifier] = useState({
    values: {
      baseLanguage: {
        name: "",
      },
      price: 0,
      imageURL: null,
      sku: "",
      minimumOrderQuantity: 1,
      maximumOrderQuantity: 1,
      isStockEnabled: false,
      availableStock: 0,
      lowStockThreshold: 0,
    },
    errors: {
      baseLanguage: {
        name: "",
      },
      price: "",
      minimumOrderQuantity: "",
      maximumOrderQuantity: "",
      isStockEnabled: "",
      availableStock: "",
      lowStockThreshold: "",
    },
  });

  /* A function that is used to set the basic info in the state. */
  const handleBasicInfo = (key, val) => {
    setModifier((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        baseLanguage: {
          ...prevState.values.baseLanguage,
          [key]: val,
        },
        translations: {
          en: {
            name: val,
          },
          ar: {
            name: val,
          },
        },
      },
      errors: {
        ...prevState.errors,
        baseLanguage: {
          ...prevState.errors.baseLanguage,
          [key]: "",
        },
      },
    }));
  };

  /* A function that is used to set the basic info in the state. */
  const handleValues = (key, val) => {
    setModifier((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [key]: val,
      },
      errors: {
        ...prevState.errors,
        [key]: "",
      },
    }));
  };

  const validateInputs = () => {
    let isValid = true;
    let obj = {
      baseLanguage: {
        name: "",
      },
      price: "",
      minimumOrderQuantity: null,
      maximumOrderQuantity: "",
    };

    if (
      !modifier?.values?.baseLanguage?.name ||
      UTILS.hasWhiteSpace(modifier.values.baseLanguage.name)
    ) {
      isValid = false;
      obj.baseLanguage.name = t("Enter modifier Name");
    }

    if (!isValidValue(modifier?.values?.price)) {
      isValid = false;
      obj.price = t("Price is a required field");
    }

    if (!isValidValue(modifier?.values?.maximumOrderQuantity)) {
      isValid = false;
      obj.maximumOrderQuantity = t("Maximum Order Quantity is a requird field");
    }
    if (modifier?.values?.maximumOrderQuantity <= 0) {
      isValid = false;
      obj.maximumOrderQuantity = t(
        "Maximum Order Quantity must be greater zero"
      );
    }

    setModifier((prevState) => ({
      ...prevState,
      errors: obj,
    }));
    return isValid;
  };

  const updatedSingleModifier = async () => {
    const isValid = validateInputs();
    if (!isValid) return;
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const data = await upDateModifier({
        brandId: userDetail.brandId,
        modifierId: history.location.state.modifier._id,
        ...modifier?.values,
        price: +parseFloat(modifier?.values?.price).toFixed(2),
      });
      if (data?.data?.success === 1) {
        if (as === "modal") {
          onSuccess(data);
          return;
        }
        UTILS.successNotification(
          data?.data?.message ?? t("Modifier Updated Successfully")
        );
        history.push("/app/dashboard/modifiers-list");
      } else {
        UTILS.errorNotification(errorHandler(data?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
    }
  };

  const getSingleModifierInfo = async (modifierId) => {
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const data = await getModifierOptionInfo({
        brandId: userDetail?.brandId,
        modifierId,
      });
      if (data?.data?.success === 1) {
        setModifier((prevState) => ({
          ...prevState,
          values: {
            ...prevState.values,
            ...data.data?.data?.[0],
          },
        }));
      } else {
        UTILS.errorNotification(errorHandler(data?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = (key, val) => {
    const value = UTILS.NumberConversion(val);
    let Num = Number(value);
    handleValues(key, Num);
  };

  useEffect(() => {
    if (history.location?.state?.modifier?._id || selectedModifier?._id)
      getSingleModifierInfo(history?.location?.state?.modifier?._id || selectedModifier?._id);
  }, [history?.location?.state?.modifier?._id, selectedModifier?._id]);

  const Tag = as === "modal" ? AppModal : AppContainer;

  return (
    <Tag
      loading={loading}
      {...(as === "modal"
        ? {
            onClose: onClose,
            open: open,
            title: "Update Customization Option",
            subTitle: subTitle,
          }
        : {
            title:
              title !== null && title !== undefined ? title : "Update Modifier",
            subTitle:
              subTitle !== null && subTitle !== undefined
                ? subTitle
                : "Please enter complete details for better user experience",
          })}
    >
      <ModifierForm
        modifier={modifier}
        handleBasicInfo={handleBasicInfo}
        handleValues={handleValues}
        onSave={updatedSingleModifier}
        updatePrice={updatePrice}
        showBackButton={as === "modal" ? false : true}
        as={as}
      />
    </Tag>
  );
};

UpdateModifier.propTypes = {
  handleValues: PropTypes.func,
  onClose: PropTypes.func,
  addModifierOption: PropTypes.func,
};
export default UpdateModifier;
